// Each mounted page owns its listeners, observers and animation work.
export function createLifecycle() {
  const cleanups = []
  const frames = new Set()
  const timers = new Set()
  let disposed = false
  return {
    on(target, type, listener, options) {
      target.addEventListener(type, listener, options)
      cleanups.push(() => target.removeEventListener(type, listener, options))
    },
    observe(Observer, callback, options) {
      const observer = new Observer(callback, options)
      cleanups.push(() => observer.disconnect())
      return observer
    },
    frame(callback) {
      if (disposed) return 0
      const id = requestAnimationFrame((time) => {
        frames.delete(id)
        if (!disposed) callback(time)
      })
      frames.add(id)
      return id
    },
    cancelFrame(id) {
      cancelAnimationFrame(id)
      frames.delete(id)
    },
    timeout(callback, delay) {
      if (disposed) return 0
      const id = setTimeout(() => {
        timers.delete(id)
        if (!disposed) callback()
      }, delay)
      timers.add(id)
      return id
    },
    clearTimeout(id) {
      clearTimeout(id)
      timers.delete(id)
    },
    cleanup(callback) {
      cleanups.push(callback)
      return () => {
        const index = cleanups.indexOf(callback)
        if (index >= 0) cleanups.splice(index, 1)
      }
    },
    dispose() {
      if (disposed) return
      disposed = true
      cleanups
        .splice(0)
        .reverse()
        .forEach((cleanup) => cleanup())
      frames.forEach(cancelAnimationFrame)
      timers.forEach(clearTimeout)
    },
  }
}

export function initializeTheme() {
  const lifecycle = createLifecycle()
  const root = document.documentElement
  // Every visit starts light; only the page's theme button changes it.
  root.dataset.theme = 'light'
  let orbit
  const motion = matchMedia('(prefers-reduced-motion: reduce)')
  let disposed = false
  let requestedTheme = root.dataset.theme,
    transition = null,
    fallbackTimer,
    generation = 0
  function syncButton() {
    const button = document.getElementById('theme-toggle')
    if (!button) return
    const light = root.dataset.theme === 'light'
    button.setAttribute('aria-pressed', String(light))
    button.setAttribute(
      'aria-label',
      light ? '切换到深色模式' : '切换到浅色模式'
    )
    button.title = light
      ? '切换到深色模式 · 三潭印月'
      : '切换到浅色模式 · 湖山见晴'
    const colorScheme = document.querySelector('meta[name="color-scheme"]')
    if (colorScheme) colorScheme.content = light ? 'light' : 'dark'
  }
  function commit(theme) {
    if (disposed) return
    if (root.dataset.theme === theme) return
    root.dataset.theme = theme
    syncButton()
    window.dispatchEvent(new Event('footprint-themechange'))
  }
  function settle() {
    generation++
    transition?.skipTransition()
    lifecycle.clearTimeout(fallbackTimer)
    delete root.dataset.themeTransition
    document.querySelectorAll('.theme-canvas-fade').forEach((el) => el.remove())
    commit(requestedTheme)
    orbit?.turn(requestedTheme)
  }
  // A small, inert copy preserves the outgoing header colors while sharing the
  // actual snow/orbit coordinates. Shadow DOM scopes SVG IDs; inert hides duplicate controls.
  function preserveHeader(header, x, y) {
    const rect = header.getBoundingClientRect()
    const copy = header.cloneNode(true)
    const sources = [header].concat(Array.from(header.querySelectorAll('*')))
    const copies = [copy].concat(Array.from(copy.querySelectorAll('*')))
    const pairs = new Map()
    // Copy only layout and paint, never the animated SVG geometry. Limiting these
    // reads keeps a theme click short enough for the existing snowfall clock.
    const properties = [
      'box-sizing',
      'display',
      'position',
      'inset',
      'width',
      'height',
      'min-width',
      'min-height',
      'max-width',
      'max-height',
      'margin',
      'padding',
      'border',
      'border-radius',
      'background',
      'box-shadow',
      'outline',
      'outline-offset',
      'color',
      'font-family',
      'font-size',
      'font-weight',
      'font-style',
      'font-variant-numeric',
      'line-height',
      'letter-spacing',
      'white-space',
      'text-align',
      'text-decoration',
      'text-transform',
      'vertical-align',
      'overflow',
      'opacity',
      'visibility',
      'filter',
      'flex',
      'flex-direction',
      'flex-wrap',
      'justify-content',
      'align-items',
      'align-self',
      'align-content',
      'gap',
      'grid-template-columns',
      'grid-template-rows',
      'grid-column',
      'grid-row',
      'fill',
      'fill-rule',
      'fill-opacity',
      'stroke',
      'stroke-width',
      'stroke-linecap',
      'stroke-linejoin',
      'stroke-dasharray',
      'stroke-dashoffset',
      'stroke-opacity',
      'stop-color',
      'stop-opacity',
      'clip-path',
      'mask',
      'paint-order',
      'vector-effect',
      'transform-box',
      'transform-origin',
    ]
    sources.forEach((source, index) => {
      const target = copies[index],
        style = getComputedStyle(source)
      target.style.cssText = properties
        .map((name) => `${name}:${style.getPropertyValue(name)};`)
        .join('')
      target.style.setProperty('transition', 'none', 'important')
      target.style.setProperty('animation', 'none', 'important')
      target.style.setProperty('view-transition-name', 'none')
      // Computed SVG paints may expand local references into document URLs.
      for (const name of ['fill', 'stroke', 'clip-path', 'mask']) {
        const value = style.getPropertyValue(name)
        if (value.includes('url('))
          target.style.setProperty(
            name,
            value.replace(/url\([^#]*#([^)"]+)"?\)/g, 'url(#$1)')
          )
      }
      pairs.set(source, target)
    })
    const layer = document.createElement('div')
    layer.className = 'theme-header-before'
    layer.setAttribute('aria-hidden', 'true')
    layer.inert = true
    layer.style.cssText = `position:fixed;left:${rect.x}px;top:${rect.y}px;width:${rect.width}px;height:${rect.height}px;pointer-events:none;z-index:1;`
    layer.style.setProperty('--theme-origin-x', `${x - rect.x}px`)
    layer.style.setProperty('--theme-origin-y', `${y - rect.y}px`)
    copy.style.position = 'absolute'
    copy.style.inset = '0'
    copy.style.margin = '0'
    layer.attachShadow({ mode: 'closed' }).append(copy)
    const observer = new MutationObserver((records) => {
      for (const { target, attributeName } of records) {
        const value = target.getAttribute(attributeName)
        if (value !== null)
          pairs.get(target)?.setAttribute(attributeName, value)
      }
    })
    observer.observe(header, {
      subtree: true,
      attributes: true,
      attributeFilter: ['cx', 'cy', 'r', 'd', 'transform'],
    })
    return {
      mount: () => header.append(layer),
      animate: (frames, options) => layer.animate(frames, options),
      remove: () => {
        observer.disconnect()
        layer.remove()
      },
    }
  }
  function reveal() {
    if (disposed) return
    if (motion.matches || document.hidden) {
      settle()
      return
    }
    if (transition || root.dataset.theme === requestedTheme) return
    if (!document.startViewTransition || !window.CSS?.registerProperty) {
      root.dataset.themeTransition = 'fade'
      commit(requestedTheme)
      orbit?.turn(requestedTheme)
      lifecycle.clearTimeout(fallbackTimer)
      fallbackTimer = lifecycle.timeout(() => {
        delete root.dataset.themeTransition
      }, 600)
      return
    }
    const button = document.getElementById('theme-toggle')
    const rect = button?.getBoundingClientRect()
    const header = document.querySelector('.page-header')
    if (!header) {
      commit(requestedTheme)
      orbit?.turn(requestedTheme)
      return
    }
    // Use the same sun/moon sky, including keyboard activation and scrolled pages.
    const x = rect
      ? Math.max(0, Math.min(innerWidth, rect.x + rect.width / 2))
      : innerWidth
    const y = rect
      ? Math.max(0, Math.min(innerHeight, rect.y + rect.height / 3))
      : 0
    const feather = Math.min(140, Math.max(64, innerWidth * 0.09))
    const radius =
      Math.hypot(Math.max(x, innerWidth - x), Math.max(y, innerHeight - y)) +
      feather
    root.style.setProperty('--theme-origin-x', `${x}px`)
    root.style.setProperty('--theme-origin-y', `${y}px`)
    root.style.setProperty('--theme-feather', `${feather}px`)
    const headerBottom = header.getBoundingClientRect().bottom
    root.style.setProperty(
      '--theme-header-bottom',
      `${Math.max(0, headerBottom)}px`
    )
    const outgoingHeader = preserveHeader(header, x, y)
    const releaseHeader = lifecycle.cleanup(() => outgoingHeader.remove())
    root.dataset.themeTransition = 'reveal'
    const theme = requestedTheme
    const captureGeneration = ++generation
    let animation, headerAnimation
    const current = document.startViewTransition(() => {
      commit(
        captureGeneration !== generation || motion.matches || document.hidden
          ? requestedTheme
          : theme
      )
      outgoingHeader.mount()
    })
    transition = current
    current.ready
      .then(() => {
        if (motion.matches || document.hidden) {
          settle()
          return
        }
        const frames = { '--theme-reveal-radius': ['0px', `${radius}px`] }
        const timing = {
          duration: 1500,
          easing: 'cubic-bezier(.4, 0, .2, 1)',
          fill: 'forwards',
        }
        animation = root.animate(frames, {
          ...timing,
          pseudoElement: '::view-transition-new(root)',
        })
        headerAnimation = outgoingHeader.animate(frames, timing)
        // Both pending animations start on the same rendering frame. The timeline
        // is stale during capture; assigning it as startTime skips the opening.
        orbit?.turn(root.dataset.theme)
      })
      .catch(() => {
        /* A skipped/unsupported capture still commits the theme. */
        if (!disposed) orbit?.turn(root.dataset.theme)
      })
    current.finished
      .catch(() => {})
      .finally(() => {
        animation?.cancel()
        headerAnimation?.cancel()
        outgoingHeader.remove()
        releaseHeader()
        if (transition !== current) return
        transition = null
        delete root.dataset.themeTransition
        // Coalesce rapid clicks into the latest destination without flashing snapshots.
        reveal()
      })
  }
  function apply(theme) {
    if (theme === requestedTheme) return
    requestedTheme = theme
    reveal()
  }
  lifecycle.on(motion, 'change', () => {
    if (motion.matches) settle()
  })
  lifecycle.on(document, 'visibilitychange', () => {
    if (document.hidden) settle()
  })
  lifecycle.on(window, 'resize', () => {
    if (transition) settle()
  })
  function createOrbit(button) {
    const sun = button.querySelector('.theme-sun')
    const moon = button.querySelector('.theme-moon')
    const motion = matchMedia('(prefers-reduced-motion: reduce)')
    let theme = root.dataset.theme
    let angle = theme === 'light' ? 0 : -Math.PI
    let target = angle,
      velocity = 0,
      frame = 0,
      previousTime = 0
    function paint() {
      // Right-hand rise, left-hand sunset; the fixed clip hides both below water.
      const phase = angle - (65 * Math.PI) / 180
      const x = Math.cos(phase) * 25,
        y = Math.sin(phase) * 23
      sun.setAttribute('transform', `translate(${32 + x} ${34 + y})`)
      moon.setAttribute('transform', `translate(${32 - x} ${34 - y})`)
    }
    function finish() {
      lifecycle.cancelFrame(frame)
      frame = 0
      velocity = 0
      angle = target %= 2 * Math.PI
      button.removeAttribute('data-orbit-moving')
      paint()
    }
    function step(now) {
      if (motion.matches || document.hidden) {
        finish()
        return
      }
      const dt = Math.min((now - previousTime) / 1000, 0.05)
      previousTime = now
      // Bounded angular speed and acceleration keep even rapid clicks readable.
      // Braking distance determines the slowdown; position and speed never reset.
      const remaining = angle - target,
        acceleration = 20
      const desiredSpeed = Math.min(
        5.5,
        Math.sqrt(2 * acceleration * remaining)
      )
      velocity += Math.max(
        -acceleration * dt,
        Math.min(acceleration * dt, desiredSpeed - velocity)
      )
      const travel = Math.min(remaining, velocity * dt)
      angle -= travel
      paint()
      if (remaining - travel < 0.0005) finish()
      else frame = lifecycle.frame(step)
    }
    function turn(nextTheme) {
      if (nextTheme === theme) return
      theme = nextTheme
      target -= Math.PI
      // Drop only redundant full turns, preserving the last requested theme.
      if (angle - target > 3 * Math.PI) target += 2 * Math.PI
      if (motion.matches || document.hidden) {
        finish()
        return
      }
      button.setAttribute('data-orbit-moving', 'true')
      if (!frame) {
        previousTime = performance.now()
        frame = lifecycle.frame(step)
      }
    }
    lifecycle.on(motion, 'change', () => {
      if (motion.matches) finish()
    })
    lifecycle.on(document, 'visibilitychange', () => {
      if (document.hidden) finish()
    })
    paint()
    lifecycle.cleanup(() => lifecycle.cancelFrame(frame))
    return { turn }
  }
  {
    syncButton()
    const button = document.getElementById('theme-toggle')
    orbit = createOrbit(button)
    button.disabled = false
    lifecycle.on(button, 'click', () => {
      const theme = requestedTheme === 'light' ? 'dark' : 'light'
      apply(theme)
    })
  }
  lifecycle.cleanup(() => {
    disposed = true
    generation++
    transition?.skipTransition()
    delete root.dataset.themeTransition
  })
  return () => lifecycle.dispose()
}

export function initializeSnow(
  root = document,
  {
    interactive = true,
    updateFavicon = true,
    softSnow = false,
    startBare = false,
  } = {}
) {
  const lifecycle = createLifecycle()
  const button = root.querySelector('.brand-weather')
  const svg = button?.querySelector('.brand-logo')
  if (!svg) return
  const ns = 'http://www.w3.org/2000/svg'
  const motion = matchMedia('(prefers-reduced-motion: reduce)')
  const back = svg.querySelector('.snow-back'),
    front = svg.querySelector('.snow-front')
  const beds = svg.querySelector('.snow-beds'),
    caps = svg.querySelector('.snow-caps')
  const make = (tag, attrs, parent) => {
    const node = document.createElementNS(ns, tag)
    Object.entries(attrs).forEach(([name, value]) =>
      node.setAttribute(name, value)
    )
    parent.append(node)
    return node
  }
  // These open paths follow the upward-facing edges of the original logo.
  // The falling snow collides with their top envelope, never a rectangular mask.
  const edges = [
    'M536 25C580 45 701 48 745 26',
    'M461 94C494 110 522 113 555 113H726C764 113 790 105 816 91',
    'M217 113C261 141 295 119 317 84C337 119 373 141 425 113',
    'M141 200C202 225 295 225 317 181C338 225 432 226 496 200',
    'M518 318C518 249 571 194 639 194C707 194 760 249 760 318',
    'M796 445C884 445 952 404 1034 345',
    'M31 407C88 454 135 445 207 445',
  ]
  const surfaces = edges.map((d) => {
    const guide = make(
      'path',
      { d, class: 'snow-guide' },
      svg.querySelector('defs')
    )
    const length = guide.getTotalLength(),
      count = Math.ceil(length / 9)
    const points = Array.from({ length: count + 1 }, (_, i) => {
      const p = guide.getPointAtLength((length * i) / count)
      return { x: p.x, y: p.y, depth: 0 }
    })
    return { points, cap: make('path', {}, caps), bed: make('path', {}, beds) }
  })
  function surfaceAt(x) {
    let hit = null
    for (const surface of surfaces) {
      const ps = surface.points
      if (x < ps[0].x || x > ps[ps.length - 1].x) continue
      for (let i = 1; i < ps.length; i++) {
        if (ps[i].x < x) continue
        const a = ps[i - 1],
          b = ps[i],
          fraction = (x - a.x) / (b.x - a.x || 1)
        const y = a.y - a.depth + (b.y - b.depth - a.y + a.depth) * fraction
        if (!hit || y < hit.y) hit = { surface, x, y }
        break
      }
    }
    return hit
  }
  function drawSurface(surface) {
    const trace = (points, move = 'M') => {
      const pair = ([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`
      if (!startBare || points.length < 3)
        return move + points.map(pair).join('L')
      let path = move + pair(points[0])
      for (let i = 1; i < points.length - 1; i++) {
        const a = points[i],
          b = points[i + 1]
        path += `Q${pair(a)} ${pair([(a[0] + b[0]) / 2, (a[1] + b[1]) / 2])}`
      }
      return path + 'L' + pair(points[points.length - 1])
    }
    let snow = '',
      shadow = ''
    const ps = surface.points
    // Each deposited patch grows along the contour; untouched spans stay bare.
    for (let start = 0; start < ps.length; start++) {
      if (ps[start].depth <= 0.05) continue
      let end = start
      while (end + 1 < ps.length && ps[end + 1].depth > 0.05) end++
      const run = ps.slice(Math.max(0, start - 1), Math.min(ps.length, end + 2))
      snow += trace(run.map((p) => [p.x, p.y - p.depth]))
      snow +=
        trace(
          [...run]
            .reverse()
            .map((p) => [p.x, p.y + Math.min(p.depth * 0.95, 22)]),
          'L'
        ) + 'Z'
      shadow += trace(run.map((p) => [p.x, p.y]))
      shadow +=
        trace(
          [...run]
            .reverse()
            .map((p) => [p.x, p.y + Math.min(p.depth * 0.85 + 1.5, 21)]),
          'L'
        ) + 'Z'
      start = end
    }
    surface.cap.setAttribute('d', snow)
    surface.bed.setAttribute('d', shadow)
  }
  function deposit(hit, radius) {
    for (const p of hit.surface.points) {
      const distance = Math.abs(p.x - hit.x),
        spread = radius * (startBare ? 9 : 6)
      if (distance >= spread) continue
      const weight = (1 + Math.cos((Math.PI * distance) / spread)) / 2
      // Snow rises above the ledge and drapes over part of its blue face.
      p.depth = Math.min(
        startBare ? 20 : 24,
        p.depth + radius * (startBare ? 0.65 : 0.85) * weight
      )
    }
    drawSurface(hit.surface)
  }
  // One softly irregular snow-grain silhouette at every depth; no icon flakes.
  const grain =
    'M-.2-1C.35-1 .85-.6 .8-.1C1 .5 .4 1-.15.9C-.8 1-1 .3-.8-.2C-.9-.7-.65-1-.2-1Z'
  const flakes = Array.from({ length: softSnow ? 30 : 8 }, (_, index) => {
    const distant = index < (softSnow ? 12 : 2)
    const depth = softSnow ? (distant ? 0.2 : 0.55) + Math.random() * 0.35 : 1
    const baseRadius = softSnow ? 2 + depth * 1.6 : distant ? 4.4 : 6.8
    const node = softSnow
      ? make(
          'path',
          {
            d: grain,
            class: 'snow-grain',
            opacity: 0,
          },
          distant ? back : front
        )
      : make('circle', { r: baseRadius }, distant ? back : front)
    return {
      node,
      baseRadius,
      depth,
      lane: index,
      pixelRadius: softSnow
        ? distant
          ? 0.65 + depth * 0.9
          : 1.15 + depth * 1.4
        : distant
        ? 0.7
        : 1.1,
      radius: baseRadius,
      x: 0,
      y: 0,
      speed: 0,
      phase: 0,
    }
  })
  const measureScale = () =>
    Math.max(
      0.01,
      svg.getBoundingClientRect().width / svg.viewBox.baseVal.width
    )
  let scale = measureScale()
  function size(flake) {
    flake.renderRadius = (flake.pixelRadius * flake.variation) / scale
    if (!softSnow) flake.node.setAttribute('r', flake.renderRadius.toFixed(2))
    flake.speed = softSnow ? flake.fallSpeed : flake.pixelSpeed / scale
  }
  function paint(flake) {
    if (softSnow) {
      // A slight turn and foreshortening, not synchronized icon-like spinning.
      const tilt = 0.7 + 0.3 * Math.abs(Math.cos((flake.angle * Math.PI) / 180))
      flake.node.setAttribute(
        'transform',
        `translate(${flake.x.toFixed(1)} ${flake.y.toFixed(
          1
        )}) rotate(${flake.angle.toFixed(1)}) scale(${(
          flake.renderRadius * tilt
        ).toFixed(2)} ${flake.renderRadius.toFixed(2)})`
      )
    } else {
      flake.node.setAttribute('cx', flake.x.toFixed(1))
      flake.node.setAttribute('cy', flake.y.toFixed(1))
    }
  }
  function reset(flake, initial = false) {
    const variation = Math.random()
    flake.variation = 1 + variation * 0.2
    // Deposition uses the original small radius, independent of screen visibility.
    flake.radius = flake.baseRadius * flake.variation
    flake.pixelSpeed = 4.5 + Math.random() * 2.5
    if (softSnow) {
      flake.fallSpeed = 38 + flake.depth * 38 + Math.random() * 12
      flake.angle = Math.random() * 360
      flake.spin = (Math.random() - 0.5) * 12
    }
    size(flake)
    // Separated horizontal lanes and staggered heights prevent clumps.
    flake.x = 65 + (flake.lane + Math.random() * 0.55) * 112
    flake.y = initial
      ? -160 + ((flake.lane * 3) % flakes.length) * 82
      : -200 - Math.random() * 220
    flake.phase = Math.random() * Math.PI * 2
    if (softSnow) {
      flake.x = 35 + (flake.lane + Math.random()) * (980 / flakes.length)
      flake.y =
        initial && !startBare
          ? -230 + Math.random() * 750
          : -35 - Math.random() * 330
    }
    paint(flake)
  }
  flakes.forEach((flake) => reset(flake, true))
  let resizeFrame = 0
  lifecycle
    .observe(ResizeObserver, () => {
      lifecycle.cancelFrame(resizeFrame)
      resizeFrame = lifecycle.frame(() => {
        const nextScale = measureScale()
        if (nextScale !== scale) {
          scale = nextScale
          flakes.forEach(size)
        }
      })
    })
    .observe(svg)
  let paused = false,
    inView = !startBare,
    frame = 0,
    lastTime = 0,
    elapsed = 0
  function running() {
    return !paused && !motion.matches && !document.hidden && inView
  }
  function step(now) {
    frame = 0
    // Also observe motion at the frame boundary: some engines update the media
    // query before delivering its change event. Keep controls and snow in sync.
    if (motion.matches) {
      if (!startBare) staticSnow()
      sync()
      return
    }
    if (!running()) {
      lastTime = 0
      return
    }
    if (!lastTime) lastTime = now
    // Limit to 30 fps; background time is never counted as accumulated snowfall.
    if (now - lastTime >= 1000 / 30) {
      const dt = Math.min((now - lastTime) / 1000, 0.08)
      lastTime = now
      elapsed += dt
      for (const flake of flakes) {
        const oldY = flake.y
        flake.y += flake.speed * dt
        flake.x += softSnow
          ? (Math.sin(elapsed * 0.35) * 9 +
              Math.sin(elapsed * 0.8 + flake.phase) * (10 + flake.depth * 18)) *
            dt
          : Math.sin(elapsed * 0.7 + flake.phase) * dt * 7
        if (softSnow) flake.angle += flake.spin * dt
        const hit = surfaceAt(flake.x)
        if (hit && oldY <= hit.y && flake.y >= hit.y) {
          deposit(hit, flake.radius)
          reset(flake)
        } else if (flake.y > 550) reset(flake)
        paint(flake)
        flake.node.setAttribute(
          'opacity',
          (
            Math.max(
              0,
              Math.min(1, (flake.y + 300) / 110, (550 - flake.y) / 85)
            ) * (softSnow ? 0.3 + flake.depth * 0.5 : 1)
          ).toFixed(2)
        )
      }
    }
    frame = lifecycle.frame(step)
  }
  function sync() {
    lifecycle.cancelFrame(frame)
    frame = 0
    lastTime = 0
    if (interactive) {
      button.disabled = motion.matches
      button.setAttribute('aria-pressed', String(paused || motion.matches))
      const label = motion.matches
        ? startBare
          ? '已减少动态效果'
          : '静态积雪，已减少动态效果'
        : paused
        ? '继续落雪'
        : '暂停落雪'
      button.setAttribute('aria-label', label)
      button.title = label
    }
    back.style.display = front.style.display = motion.matches ? 'none' : ''
    if (running()) frame = lifecycle.frame(step)
  }
  function staticSnow() {
    // A visible thin cap rests on the letter face as well as above its edge.
    // The same initial depth and deposition geometry serve both themes.
    surfaces.forEach((surface) => {
      surface.points.forEach((p) => {
        const top = surfaceAt(p.x)
        if (top?.surface === surface && p.depth < 7) p.depth = 7
      })
      drawSurface(surface)
    })
  }
  function syncFavicon() {
    if (!updateFavicon) return
    const icon = document.querySelector('link[rel="icon"]')
    if (!icon) return
    // Use the header's real geometry and snow; update only on load/theme changes.
    // Falling flakes never cause per-frame favicon work or browser-tab repaints.
    const copy = svg.cloneNode(true)
    copy.removeAttribute('class')
    copy
      .querySelectorAll('.snow-back, .snow-front, .snow-guide')
      .forEach((node) => node.remove())
    const css = getComputedStyle(document.documentElement)
    const color = (name) => css.getPropertyValue(`--color-${name}`).trim()
    copy
      .querySelector('.logo-art')
      .setAttribute(
        'fill',
        document.documentElement.dataset.theme === 'light'
          ? color('brand')
          : 'url(#brand-blue)'
      )
    copy
      .querySelectorAll('#brand-blue stop')
      .forEach((stop, index) =>
        stop.setAttribute(
          'stop-color',
          color(['logo-top', 'logo-mid', 'logo-base'][index])
        )
      )
    for (const selector of ['.snow-beds', '.snow-caps']) {
      const source = getComputedStyle(svg.querySelector(selector)),
        target = copy.querySelector(selector)
      for (const property of [
        'fill',
        'opacity',
        'stroke',
        'stroke-width',
        'stroke-linejoin',
      ])
        target.setAttribute(property, source.getPropertyValue(property))
    }
    icon.href = `data:image/svg+xml,${encodeURIComponent(
      new XMLSerializer().serializeToString(copy)
    )}`
  }
  lifecycle.on(window, 'footprint-themechange', syncFavicon)
  if (interactive)
    lifecycle.on(button, 'click', () => {
      paused = !paused
      sync()
    })
  lifecycle.on(document, 'visibilitychange', sync)
  lifecycle.on(motion, 'change', () => {
    if (motion.matches && !startBare) staticSnow()
    sync()
  })
  lifecycle
    .observe(
      IntersectionObserver,
      (entries) => {
        const visible =
          entries[0].isIntersecting &&
          (!startBare || entries[0].intersectionRatio >= 0.15)
        if (visible === inView) return
        inView = visible
        sync()
      },
      { threshold: startBare ? [0, 0.15] : 0 }
    )
    .observe(button)
  // The homepage opens bare; only actual landings create its snow cover.
  // The standalone header retains its original initial dusting.
  if (!startBare) staticSnow()
  syncFavicon()
  sync()

  lifecycle.cleanup(() => {
    back.replaceChildren()
    front.replaceChildren()
    beds.replaceChildren()
    caps.replaceChildren()
    svg.querySelectorAll('.snow-guide').forEach((node) => node.remove())
  })
  return () => lifecycle.dispose()
}
